import { ProfilePictureLight } from '@/components/profile-picture/profile-picture.light';
import { SkeletonLight } from '@/components/skeleton/skeleton.light';
import { useGetPersonalInfo } from '@/hooks/api';
import { normalizedFirstName } from '@/lib';
import { ArrowLeft } from '@nosinovacao/fe-icons';
import { Button, cn, Sheet, useI18n, useInMediaQuery } from '@nosinovacao/nosid-mfe-common';
import { createContext, type ReactNode, useContext, useState } from 'react';
import { SettingsCodeInputSection } from './code-input-section';
import { SettingsConsentDetailSection, SettingsConsentsSection } from './consents-section';
import { SettingsContactSelectionSection } from './contact-selection-section';
import { SettingsDeleteAccountConfirmSection } from './delete-account-section';
import { SettingsLanguageSection } from './language-section';
import { SettingsMainSection } from './main-section';
import { type SettingsStep, type StepConfig } from './models';
import { UpdatePersonalInfoSection } from './update-personal-info-section';

interface NavigationState {
  step: SettingsStep;
  params?: Record<string, unknown>;
}

const UserSettingsContext = createContext({ open: false, setOpen: (_: boolean) => {} });

const stepsConfiguration: Record<Exclude<SettingsStep, 'delete-account'>, StepConfig> = {
  main: {
    component: SettingsMainSection,
    withBack: false,
  },
  'update-personal-info': {
    withBack: true,
    title: 'Common.UpdatePersonalInfo.Title',
    component: UpdatePersonalInfoSection,
  },
  language: {
    component: SettingsLanguageSection,
    title: 'Common.Language.Title',
    withBack: true,
  },
  consents: {
    component: SettingsConsentsSection,
    title: 'Common.Consents.CardTitle',
    withBack: true,
  },
  'consent-detail': {
    component: SettingsConsentDetailSection,
    withBack: true,
  },
  'contact-selection': {
    component: SettingsContactSelectionSection,
    title: 'Common.ContactValidation.ContactSelection.SheetTitle',
    withBack: true,
  },
  'code-input': {
    component: SettingsCodeInputSection,
    title: 'Common.ContactValidation.CodeInput.SheetTitle',
    withBack: true,
  },
  'delete-account-confirm': {
    component: SettingsDeleteAccountConfirmSection,
    title: 'Common.DeleteAccount.Confirm.SheetTitle',
    withBack: true,
  },
};

export function UserSettingsSheet({ children }: Readonly<{ children?: ReactNode }>) {
  const [open, setOpen] = useState(false);
  const value = { open, setOpen };
  return (
    <UserSettingsContext.Provider value={value}>
      {children}
      <UserSettingsContent />
    </UserSettingsContext.Provider>
  );
}

export function UserSettingsContent() {
  const { open, setOpen } = useContext(UserSettingsContext);
  const [navigationStack, setNavigationStack] = useState<NavigationState[]>([{ step: 'main' }]);
  const { t } = useI18n();

  const currentNav = navigationStack.at(-1)!;
  const {
    component: StepComponent,
    title,
    subtitle,
    withBack,
  } = stepsConfiguration[currentNav.step as Exclude<SettingsStep, 'delete-account'>];

  const resolvedTitle =
    (currentNav.params?.title as string | undefined) ?? (title ? t(title) : undefined);

  const resolvedSubtitle =
    (currentNav.params?.subtitle as string | undefined) ?? (subtitle ? t(subtitle) : undefined);

  const goToStep = (step: SettingsStep, params?: Record<string, unknown>) => {
    if (step === 'delete-account') {
      setNavigationStack((prev) => [...prev, { step: 'contact-selection' }]);
      return;
    }
    setNavigationStack((prev) => [...prev, { step, params }]);
  };

  const goBack = () => {
    setNavigationStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  };
  const goToMain = () => {
    setNavigationStack([{ step: 'main' }]);
  };

  return (
    <Sheet
      skin="light"
      className="z-[60] tablet:pb-0"
      overlayClassName="z-[60]"
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setNavigationStack([{ step: 'main' }]);
        }
        setOpen(o);
      }}
      title={resolvedTitle}
      subtitle={resolvedSubtitle}
      back={
        withBack ? (
          <Button skin="light" variant="tertiary" onClick={goBack}>
            <ArrowLeft width={20} height={20} />
            {t('Common.Back')}
          </Button>
        ) : null
      }
    >
      <StepComponent goToMain={goToMain} goToStep={goToStep} params={currentNav.params} />
    </Sheet>
  );
}

export function UserSettingsTrigger() {
  const { isLoading, data } = useGetPersonalInfo();
  const isDesktop = useInMediaQuery('desktop-md');
  const { setOpen } = useContext(UserSettingsContext);
  return (
    <SkeletonLight
      loading={isLoading}
      className="size-10 rounded-full max-desktop-md:bg-nos-green/60 max-desktop-md:before:via-nos-lime"
    >
      <button
        type="button"
        className={cn(
          'flex cursor-pointer items-center gap-2 rounded-lg transition-colors [&>*]:shrink-0',
          'outline-none desktop-md:px-[6px] desktop-md:py-1 desktop-md:-outline-offset-2',
          'desktop-md:hover:bg-nos-neutral-0 desktop-md:hover:outline-nos-green',
          'focus-visible:outline-nos-blue desktop-md:focus-visible:bg-nos-neutral-0 desktop-md:focus-visible:outline-nos-green',
        )}
        data-tid="button-user-settings"
        onClick={() => setOpen(true)}
      >
        <ProfilePictureLight size={40} name={data?.DisplayName} src={data?.PamPictureUri} />
        {data?.DisplayName && isDesktop ? (
          <span className="font-medium text-nos-neutral-900 font-text-p1">
            {normalizedFirstName(data?.DisplayName)}
          </span>
        ) : null}
      </button>
    </SkeletonLight>
  );
}
